import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import { environment } from '@rpgc-ai/environments';

export interface ISeoConfig {
  titleKey: string;
  descriptionKey: string;
  ogTitleKey?: string;
  ogDescriptionKey?: string;
  canonicalPath?: string;
}

export interface IImagePreloadConfig {
  id: string;
  href: string;
  type?: string;
  fetchPriority?: 'high' | 'low' | 'auto';
}

export interface IAlternateLinkConfig {
  hreflang: string;
  path: string;
}

export type SeoOwner = object;

@Injectable({ providedIn: 'root' })
export class SeoService {
  readonly #title = inject(Title);
  readonly #meta = inject(Meta);
  readonly #document = inject(DOCUMENT);
  #titleOwner: SeoOwner | null = null;
  readonly #tagOwners = new Map<string, SeoOwner | null>();

  setTitle(title: string, owner: SeoOwner | null = null): void {
    if (!title) {
      return;
    }
    this.#title.setTitle(title);
    this.#titleOwner = owner;
  }

  clearTitle(owner?: SeoOwner): void {
    if (owner !== undefined && this.#titleOwner !== owner) {
      return;
    }
    this.#title.setTitle(environment.brandShort);
    this.#titleOwner = null;
  }

  setTag(key: string, value: string, type: 'name' | 'property', owner: SeoOwner | null = null): void {
    if (!value) {
      return;
    }

    const selector = `${type}='${key}'`;
    this.#meta.updateTag({
      [type]: key,
      content: value,
    });
    this.#tagOwners.set(selector, owner);
  }

  removeTag(key: string, type: 'name' | 'property', owner?: SeoOwner): void {
    const selector = `${type}='${key}'`;
    if (owner !== undefined && this.#tagOwners.get(selector) !== owner) {
      return;
    }
    this.#meta.removeTag(selector);
    this.#tagOwners.delete(selector);
  }

  setCanonicalPath(path: string): void {
    if (!path) {
      return;
    }

    const domain = environment.APP_DOMAIN.replace(/^https?:\/\//, '');
    const origin = `https://${domain}`;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    let link = this.#document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!link) {
      link = this.#document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.#document.head.appendChild(link);
    }

    link.setAttribute('href', `${origin}${normalizedPath}`);
  }

  removeCanonicalPath(): void {
    this.#document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')?.remove();
  }

  setAlternatePaths(links: IAlternateLinkConfig[]): void {
    const domain = environment.APP_DOMAIN.replace(/^https?:\/\//, '');
    const origin = `https://${domain}`;
    this.removeAlternatePaths();

    for (const { hreflang, path } of links) {
      const link = this.#document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', hreflang);
      link.setAttribute('href', `${origin}${path.startsWith('/') ? path : `/${path}`}`);
      this.#document.head.appendChild(link);
    }
  }

  removeAlternatePaths(): void {
    this.#document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((element) => element.remove());
  }

  setImagePreload(config: IImagePreloadConfig): void {
    if (!config.href || !config.id) {
      return;
    }

    let link = this.#document.head.querySelector<HTMLLinkElement>(`link[data-image-preload-id="${config.id}"]`);

    if (!link) {
      link = this.#document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('data-image-preload-id', config.id);
      this.#document.head.appendChild(link);
    }

    link.setAttribute('href', config.href);

    if (config.type) {
      link.setAttribute('type', config.type);
    } else {
      link.removeAttribute('type');
    }

    if (config.fetchPriority) {
      link.setAttribute('fetchpriority', config.fetchPriority);
    } else {
      link.removeAttribute('fetchpriority');
    }
  }

  removeImagePreload(id: string): void {
    if (!id) {
      return;
    }

    this.#document.head.querySelector<HTMLLinkElement>(`link[data-image-preload-id="${id}"]`)?.remove();
  }
}
